import dayjs from "dayjs";
import { EMPTY_VALUE } from "Shared/Constants/app";
import { formatPersonName } from "./personName";

/**
 * Display helpers: one empty marker (EMPTY_VALUE, "—") for missing values.
 * They check `== null` rather than truthiness, so a real 0 renders as "0".
 */

type Primitive = string | number | null | undefined;

/** null, undefined, NaN, or a string with no visible characters. */
export const isBlank = (value: unknown): value is null | undefined | "" =>
  value == null ||
  (typeof value === "number" && Number.isNaN(value)) ||
  (typeof value === "string" && value.trim() === "");

/** The value as text, or the fallback when it is blank. */
export const displayText = (value: Primitive, fallback = EMPTY_VALUE) =>
  isBlank(value) ? fallback : String(value);

const toNumber = (value: Primitive) => {
  if (isBlank(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * A number with thousands separators ("1,204", "0", "12.5"), and an optional
 * unit after it ("12 mi"). The unit is left off the fallback, so a missing
 * value never reads "— mi".
 */
export const displayNumber = (
  value: Primitive,
  {
    unit,
    maximumFractionDigits = 2,
    fallback = EMPTY_VALUE,
  }: { unit?: string; maximumFractionDigits?: number; fallback?: string } = {},
) => {
  const parsed = toNumber(value);
  if (parsed === null) return fallback;
  const text = parsed.toLocaleString("en-US", { maximumFractionDigits });
  return unit ? `${text} ${unit}` : text;
};

/** A whole count ("12", "1,204"), never "12.00". */
export const displayCount = (value: Primitive, fallback = EMPTY_VALUE) =>
  displayNumber(value, { maximumFractionDigits: 0, fallback });

// Company settings (currency, date and time format) -------------------------

const listeners = new Set<() => void>();
/** Called when the company's currency or date settings change. */
export const subscribeDisplaySettings = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const notify = () => listeners.forEach((listener) => listener());

// Money -------------------------------------------------------------------

let companyCurrency = "USD";

/**
 * The company's currency (Company Settings). Set from the store when the
 * company loads (Redux/store.ts); USD until then or if the code is invalid.
 */
export const setDisplayCurrency = (code?: string | null) => {
  let next = code?.trim().toUpperCase() || "USD";
  try {
    new Intl.NumberFormat("en-US", { style: "currency", currency: next });
  } catch {
    next = "USD";
  }
  if (next === companyCurrency) return;
  companyCurrency = next;
  notify();
};

export const getDisplayCurrency = () => companyCurrency;

const moneyFormatters = new Map<string, Intl.NumberFormat>();
const moneyFormatter = (currency: string, wholeUnits: boolean) => {
  const key = `${currency}:${wholeUnits}`;
  let formatter = moneyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: wholeUnits ? 0 : 2,
      maximumFractionDigits: wholeUnits ? 0 : 2,
    });
    moneyFormatters.set(key, formatter);
  }
  return formatter;
};

type MoneyOptions = {
  /** Defaults to the company currency. */
  currency?: string;
  /** "$1,205" instead of "$1,204.50", for chart axes. */
  wholeUnits?: boolean;
};

/** "$1,204.50", "-$12.00", "$0.00"; null when there is no amount. */
export const formatMoney = (
  value: Primitive,
  { currency = companyCurrency, wholeUnits = false }: MoneyOptions = {},
) => {
  const parsed = toNumber(value);
  return parsed === null ? null : moneyFormatter(currency, wholeUnits).format(parsed);
};

/** formatMoney for display: the empty marker when there is no amount. */
export const displayMoney = (
  value: Primitive,
  options: MoneyOptions & { fallback?: string } = {},
) => formatMoney(value, options) ?? options.fallback ?? EMPTY_VALUE;

// Dates -------------------------------------------------------------------

export type DateKind = "date" | "dateTime" | "time";
export type DateFormats = Record<DateKind, string>;

const DATE_CHOICES = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

/** The three display formats for a company's date and time settings. */
export const toDateFormats = (
  shortDateFormat?: string | null,
  timeFormat?: string | null,
): DateFormats => {
  const date = DATE_CHOICES.includes(shortDateFormat ?? "")
    ? (shortDateFormat as string)
    : "MM/DD/YYYY";
  const time = timeFormat === "24-hour" ? "HH:mm" : "h:mm A";
  return { date, time, dateTime: `${date} ${time}` };
};

let dateFormats = toDateFormats();

/**
 * The company's date and time format (Company Settings), set from the store
 * when the company loads (Redux/store.ts). Components should read them with
 * useDisplayFormats() so they re-render when the settings change.
 */
export const setDisplayDateFormats = (
  shortDateFormat?: string | null,
  timeFormat?: string | null,
) => {
  const next = toDateFormats(shortDateFormat, timeFormat);
  if (next.dateTime === dateFormats.dateTime) return;
  dateFormats = next;
  notify();
};

export const getDisplayDateFormats = () => dateFormats;

/**
 * Calendar headers and range chips ("October 2026", "Sep 1 – 30, 2026").
 * Record dates use displayDate()/formatDate() instead.
 */
export const LABEL_FORMATS = {
  monthYear: "MMMM YYYY",
  shortMonth: "MMM",
  shortMonthYear: "MMM YYYY",
  monthDay: "MMM D",
  monthDayYear: "MMM D, YYYY",
  dayYear: "D, YYYY",
} as const;

type DateValue = string | number | Date | null | undefined;

/** A date in the company's format; null when it is missing or invalid. */
export const formatDate = (value: DateValue, kind: DateKind = "date") => {
  if (value == null || value === "") return null;
  const date = dayjs(value);
  return date.isValid() ? date.format(dateFormats[kind]) : null;
};

/** formatDate for display: the empty marker for a missing or invalid date. */
export const displayDate = (
  value: DateValue,
  kind: DateKind = "date",
  fallback = EMPTY_VALUE,
) => formatDate(value, kind) ?? fallback;

export type FieldFormat = "text" | "date" | "money" | "meter";

/** A detail-page field shown by its declared format. */
export const displayField = (value: unknown, format: FieldFormat = "text") => {
  const primitive =
    typeof value === "string" || typeof value === "number" ? value : null;
  switch (format) {
    case "date":
      return displayDate(primitive);
    case "money":
      return displayMoney(primitive);
    case "meter":
      return displayNumber(primitive, { unit: "mi" });
    default:
      return displayText(primitive);
  }
};

/** "First Last", or the fallback for a missing person. */
export const displayName = (
  person?: { first_name?: string | null; last_name?: string | null } | null,
  fallback = EMPTY_VALUE,
) => formatPersonName(person, fallback);

export { EMPTY_VALUE };
